import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import { SelectedUserPrompt, UserSavedPrompt } from '@Models/types';
import PromptList from '@Prompts/side/PromptList';
import {
  MagnifyingGlass,
  SquaresFour,
  X,
  Sidebar,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import AddPrompt from './AddPrompt';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { MoonLoader } from 'react-spinners';

interface Props {
  toggled: boolean;
  setToggleSidebar: (value: boolean) => void;
}

const PromptsSidebar: React.FC<Props> = ({ toggled, setToggleSidebar }) => {
  const { userPrompts, setSelectedPromptVersion, fetchUserPrompts } =
    usePromptToolboxContext();

  const { loaders, setView } = useUtilityContext();
  const [search, setSearch] = useState('');
  const [filteredSearch, setFilteredSearch] = useState<UserSavedPrompt[]>([]);

  useEffect(() => {
    if (userPrompts.length === 0) {
      fetchUserPrompts();
    }
  }, []);

  useEffect(() => {
    setFilteredSearch(userPrompts);
  }, [userPrompts]);

  const handleSelectVersion = (prompt: SelectedUserPrompt) => {
    setSelectedPromptVersion(prompt);
    setView('Diff');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearch(query);
    setFilteredSearch(
      userPrompts.filter((prompt) =>
        prompt.name.toLowerCase().includes(query.toLowerCase())
      )
    );
  };

  const clearSearch = () => {
    setSearch('');
    setFilteredSearch(userPrompts);
  };

  return (
    <div className="z-10 flex h-full flex-col gap-y-4 bg-gray-200 p-4 md:rounded-l-2xl">
      {/* SEARCH & ADD */}
      <div className="relative flex w-full flex-row items-center justify-between gap-x-2">
        <Sidebar
          weight="fill"
          className={`cursor-pointer text-[2.5rem] text-primary-700 md:hidden`}
          onClick={() => setToggleSidebar(!toggled)}
        />
        <div className="flex w-full flex-row items-center gap-x-2 rounded-md bg-white px-4 py-2 shadow-sm">
          <MagnifyingGlass size={20} weight="bold" />
          <input
            data-testid="search-bar"
            type="text"
            className="w-full font-inter text-sm focus:outline-none"
            placeholder="Search Prompts"
            value={search}
            onChange={handleSearch}
          />
          <X
            weight="bold"
            size={20}
            className="cursor-pointer hover:text-brandRed-500"
            onClick={clearSearch}
          />
        </div>
        <AddPrompt />
      </div>

      {/* PROMPT LIST */}
      <div className="custom-scroll flex h-full w-full flex-col gap-y-1 overflow-y-auto py-2 pr-2">
        {/* PROMPT ITEM */}
        {loaders.userPrompts ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-y-6 p-5">
            <MoonLoader size={80} color="#071D49" />
            <span
              data-testid="search-prompt-list"
              className="animate-pulse text-xs font-semibold tracking-wide text-gray-600"
            >
              User Prompts Loading
            </span>
          </span>
        ) : (
          <>
            {filteredSearch.length === 0 ? (
              <span className="flex h-full flex-col items-center justify-center text-neutral-300">
                <SquaresFour
                  size={150}
                  weight="bold"
                  className="text-neutral-400"
                />
                <label data-testid="no-result-search-message" className="text-xs font-medium tracking-wider text-neutral-500">
                  No Prompts Found
                </label>
              </span>
            ) : (
              <PromptList
                prompts={filteredSearch}
                onSelectVersion={handleSelectVersion}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromptsSidebar;
